// Subtitles. The desktop engine burns ASS via FFmpeg's libass filter — which the
// default ffmpeg.wasm core does NOT include. Two browser strategies:
//
//  A) ffmpeg.wasm with a CUSTOM libass-enabled core (heavier, keeps ASS logic).
//  B) Canvas/WebCodecs path: draw word-by-word karaoke directly onto each frame
//     during compositing (recommended — pairs with the WebCodecs render path and
//     gives pixel-accurate control without libass).
//
// This module owns timing (shared) + a canvas drawer (strategy B). ASS generation
// is kept for strategy A / export compatibility.

// duration / wordCount, min-display 0.4s — same rules as desktop karaoke.
export function computeWordTimings(words, clipStart, clipEnd) {
  const span = clipEnd - clipStart;
  const per = Math.max(0.4, span / Math.max(1, words.length));
  return words.map((w, i) => ({
    word: w,
    start: clipStart + i * per,
    end: clipStart + (i + 1) * per,
  }));
}

// Strategy B: draw the karaoke subtitle line(s) onto a 2D canvas at clip-local time t.
// Groups words into lines (maxWordsPerLine), shows the line containing the active word,
// highlights the active word. Honors the common subtitle style knobs from the desktop app.
export function drawSubtitleFrame(ctx, timings, t, opts = {}) {
  const { W = ctx.canvas.width, H = ctx.canvas.height, style = {} } = opts;
  if (style.subtitleEnabled === false || !timings.length) return;

  const activeIdx = timings.findIndex((x) => t >= x.start && t < x.end);
  if (activeIdx === -1) return;

  const maxPerLine = style.subtitleMaxWordsPerLine || 4;
  const lineStart = Math.floor(activeIdx / maxPerLine) * maxPerLine;
  const lineWords = timings.slice(lineStart, lineStart + maxPerLine);
  const activeInLine = activeIdx - lineStart;

  const fontPx = style.subtitleFontSize ? Math.round(W * (style.subtitleFontSize / 1080)) : Math.round(W * 0.062);
  const fill = style.subtitleTextColor || '#ffffff';
  const highlight = style.subtitleHighlightColor || '#00FF7F';
  const outline = style.subtitleOutlineColor || 'rgba(0,0,0,0.9)';
  const outlineW = style.subtitleOutlineWidth ? style.subtitleOutlineWidth * 1.4 : Math.max(4, fontPx * 0.12);
  const posPct = typeof style.subtitlePosition === 'number' ? style.subtitlePosition : 82; // % from top
  const upper = style.subtitleUppercase ? (s) => s.toUpperCase() : (s) => s;

  ctx.save();
  ctx.font = `bold ${fontPx}px ${style.subtitleFontFamily || 'TheBold, sans-serif'}`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.lineJoin = 'round';

  const words = lineWords.map((w) => upper(w.word));
  const gap = fontPx * 0.3;
  const widths = words.map((w) => ctx.measureText(w).width);
  const total = widths.reduce((s, w) => s + w, 0) + gap * (words.length - 1);
  let x = (W - total) / 2;
  const y = Math.round(H * (posPct / 100));

  for (let i = 0; i < words.length; i++) {
    const isActive = i === activeInLine;
    if (isActive && style.subtitleScaleEffect) {
      ctx.save();
      ctx.translate(x + widths[i] / 2, y);
      ctx.scale(style.subtitleHighlightScale || 1.1, style.subtitleHighlightScale || 1.1);
      ctx.translate(-(x + widths[i] / 2), -y);
    }
    ctx.lineWidth = outlineW;
    ctx.strokeStyle = outline;
    ctx.strokeText(words[i], x, y);
    ctx.fillStyle = isActive ? highlight : fill;
    ctx.fillText(words[i], x, y);
    if (isActive && style.subtitleScaleEffect) ctx.restore();
    x += widths[i] + gap;
  }
  ctx.restore();
}

// Strategy A: ASS generation (kept for libass-core export). BGR color tags.
export function generateASS(/* clip, timings, style */) {
  // TODO: port generateSubtitleASS() from desktop overlayRenderer.js.
  throw new Error('ASS generation not yet ported — use canvas strategy (drawSubtitleFrame).');
}
